import { FC, useEffect, useState } from 'react'
import { Select } from 'antd'
import { CheckBoxLabel } from '@UI/CheckBox'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import { directionGroupApi } from '@/api'
import spclasses from '../../../../../SetCourseClassDirectionModal/purchase.module.css'
import {
  CREATE_LINK_BLOCK_TARIFF_TYPES,
  isGroupPerLessonLinkTariff,
} from '../../../../tariffTypes'
import { withCreateLinkBlockTariffTypesHint } from '../../../../createLinkBlockInfoContent'
import { SHARED_BALANCE_GROUP_DISABLED_TOOLTIP } from '../../../CourseLinkGroupSelect/CourseLinkGroupSelect'
import groupSelectClasses from '../../../CourseLinkGroupSelect/index.module.css'

export type AdditionalCourseGroupOption = {
  value: number
  label: string
  disabled?: boolean
  lessonDuration?: number | null
  studentLessonPrice?: number | null
}

type AdditionalCourseGroupSelectProps = {
  courseId?: number | null
  tariffType?: string | null
  value?: number | null
  onChange: (
    groupId: number | null,
    meta?: AdditionalCourseGroupOption | null,
  ) => void
  /** Синк meta при загрузке options (для таблицы источников). */
  onSelectedMetaChange?: (meta: AdditionalCourseGroupOption | null) => void
}

const NO_GROUPS_TOOLTIP =
  'В выбранном курсе пока нет групп / потоков. Создайте группу в настройках курса, чтобы распределить ученика.'

/**
 * Чекбокс «Распределить ученика в группу» + селект — паритет с основным CourseLinkGroupSelect.
 * Список групп по courseId доп. курса.
 */
const AdditionalCourseGroupSelect: FC<AdditionalCourseGroupSelectProps> = ({
  courseId,
  tariffType,
  value,
  onChange,
  onSelectedMetaChange,
}) => {
  const [options, setOptions] = useState<AdditionalCourseGroupOption[]>([])
  const [loading, setLoading] = useState(false)
  const [isAssignToGroup, setIsAssignToGroup] = useState(value != null)
  const isGroupPerLesson = isGroupPerLessonLinkTariff(tariffType)
  const hasGroups = options.length > 0
  const isCheckboxDisabled = !loading && !hasGroups

  useEffect(() => {
    if (!courseId) {
      setOptions([])
      return
    }

    let cancelled = false
    setLoading(true)
    directionGroupApi
      .get({
        filter: [`[direction_id][eq]=${courseId}`],
      })
      .then((res) => {
        if (cancelled) return
        const groups = (res?.data ?? []) as Array<{
          id?: number
          name?: string | null
          teacher_name?: string | null
          type?: string | null
          group_type?: string | null
          lesson_duration?: number | null
          student_lesson_price?: number | null
          is_shared_balance?: boolean | null
        }>

        setOptions(
          groups
            .filter((group) => {
              if (group?.id == null) return false
              const isLessonPayment =
                group.type === 'lesson-payment' ||
                group.group_type === 'lesson-payment'
              if (isGroupPerLesson) return isLessonPayment
              return !isLessonPayment
            })
            .map((group) => {
              const isSharedBalance = Boolean(group.is_shared_balance)
              const disabled = isGroupPerLesson && isSharedBalance
              return {
                value: Number(group.id),
                label: [group.name, group.teacher_name]
                  .filter(Boolean)
                  .join(' — '),
                disabled,
                lessonDuration: group.lesson_duration ?? null,
                studentLessonPrice: group.student_lesson_price ?? null,
              }
            }),
        )
      })
      .catch(() => {
        if (!cancelled) setOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [courseId, isGroupPerLesson])

  useEffect(() => {
    setIsAssignToGroup(false)
    onChange(null, null)
    // Только при смене курса — сброс чекбокса/группы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId])

  useEffect(() => {
    if (isCheckboxDisabled && isAssignToGroup) {
      setIsAssignToGroup(false)
      onChange(null, null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckboxDisabled, isAssignToGroup])

  useEffect(() => {
    if (value == null) return
    const selected = options.find((item) => item.value === Number(value))
    if (!selected || selected.disabled) {
      onChange(null, null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value])

  useEffect(() => {
    if (!onSelectedMetaChange) return
    if (value == null || !isAssignToGroup) {
      onSelectedMetaChange(null)
      return
    }
    onSelectedMetaChange(
      options.find((item) => item.value === Number(value)) ?? null,
    )
  }, [onSelectedMetaChange, options, value, isAssignToGroup])

  const onToggleAssign = (checked?: boolean) => {
    if (isCheckboxDisabled) return

    const nextChecked =
      typeof checked === 'boolean' ? checked : !isAssignToGroup
    setIsAssignToGroup(nextChecked)
    if (!nextChecked) {
      onChange(null, null)
    }
  }

  const checkbox = (
    <CheckBoxLabel
      type="button"
      value={isAssignToGroup}
      disabled={isCheckboxDisabled}
      onClick={onToggleAssign}
      label="Распределить ученика в группу"
      className={spclasses.checkboxWithLabel}
      style={{
        justifyContent: 'flex-start',
        paddingTop: '4px',
        paddingBottom: '4px',
        height: 'auto',
      }}
    />
  )

  return (
    <div className={spclasses.inputWithLabel}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isCheckboxDisabled ? (
          <TooltipWrapper placement="top" title={NO_GROUPS_TOOLTIP}>
            <span className={groupSelectClasses.disabledCheckboxWrap}>
              {checkbox}
            </span>
          </TooltipWrapper>
        ) : (
          checkbox
        )}
        <InfoComp
          text={withCreateLinkBlockTariffTypesHint(
            'Можно заранее выбрать группу / поток, в которую ученик будет распределён после оплаты по ссылке.',
            CREATE_LINK_BLOCK_TARIFF_TYPES.groupSelect,
          )}
        />
      </div>

      {isAssignToGroup && !isCheckboxDisabled ? (
        <div style={{ paddingTop: '4px' }}>
          <div className={spclasses.inputLabel}>Группа / поток</div>
          <Select
            showSearch
            loading={loading}
            className={spclasses.select}
            popupClassName={groupSelectClasses.selectPopup}
            placeholder="Выберите группу / поток"
            optionFilterProp="label"
            value={value ?? undefined}
            options={options.map((item) => ({
              value: item.value,
              label: item.disabled
                ? `${item.label} (${SHARED_BALANCE_GROUP_DISABLED_TOOLTIP})`
                : item.label,
              disabled: item.disabled,
            }))}
            onChange={(next) => {
              const id = next == null ? null : Number(next)
              const meta =
                id == null
                  ? null
                  : options.find((item) => item.value === id) ?? null
              onChange(id, meta)
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

export default AdditionalCourseGroupSelect
